/**
 * Textos da avaliação (proposta "Review" do Claude Design, 17/10/2026). O português fica como
 * padrão no código (chaves `rv_*` em src/i18n/locales); en, es e fr vêm dos arquivos de idioma.
 *
 * Faixas da nota: 0–6 "baixa", 7–8 "media", 9–10 "alta". Cada faixa tem a sua pergunta e as suas
 * opções. Os motivos vão para o backend sempre em português (`REVIEW_PT`), para o Slack e os
 * relatórios lerem igual em qualquer idioma.
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export type Band = 'baixa' | 'media' | 'alta';

export function bandOf(score: number): Band {
  if (score >= 9) return 'alta';
  if (score >= 7) return 'media';
  return 'baixa';
}

export const REASONS: Record<Band, string[]> = {
  baixa: [
    'outdated',
    'few_contacts',
    'off_target',
    'capture_fails',
    'hard_to_use',
    'crm',
    'local_emails',
    'price',
  ],
  media: [
    'crm_integration',
    'more_local_emails',
    'fresher',
    'filters',
    'faster',
    'more_whatsapp',
    'price',
  ],
  alta: ['saves_time', 'pipeline', 'accurate', 'whatsapp', 'easy', 'value'],
};

export const REVIEW_PT = {
  eyebrow: 'Queremos te ouvir',
  question: 'De 0 a 10, quanto a {{product}} ajuda nas suas metas de geração de clientes?',
  close: 'Fechar',
  scale_low: '0 · Nada',
  scale_high: 'Muito · 10',
  ask_baixa: 'O que faltou para uma nota maior?',
  ask_media: 'O que faria a nota chegar a 10?',
  ask_alta: 'O que mais ajuda você?',
  more: 'Quer contar mais?',
  optional: 'Opcional',
  more_placeholder: 'Em poucas palavras',
  send: 'Enviar',
  sending: 'Enviando…',
  send_error: 'Não conseguimos enviar agora. Tente de novo em instantes.',
  thanks_title: 'Obrigado pela nota {{n}}',
  thanks_text: 'Sua resposta vai direto para quem decide as próximas melhorias da {{product}}.',
  invite_title: 'Indique e ganhe dias de uso',
  invite_text:
    'Quem gosta da {{product}} costuma ter colegas que também prospectam. Quem contratar pelo seu link ganha desconto, e você ganha dias de uso.',
  invite_yes: 'Quero indicar',
  invite_later: 'Agora não',
  // Motivos (0–6)
  r_outdated: 'Contatos desatualizados',
  r_few_contacts: 'Poucos contatos por lista',
  r_off_target: 'Contatos fora do meu público',
  r_capture_fails: 'A captura trava ou para',
  r_hard_to_use: 'Difícil de usar',
  r_crm: 'Falta integração com o CRM',
  r_local_emails: 'Poucos emails nos negócios locais',
  r_price: 'Preço',
  // Motivos (7–8)
  r_crm_integration: 'Integração com o CRM',
  r_more_local_emails: 'Mais emails nos negócios locais',
  r_fresher: 'Contatos mais atualizados',
  r_filters: 'Mais filtros na busca',
  r_faster: 'Captura mais rápida',
  r_more_whatsapp: 'Mais celulares com WhatsApp',
  // Motivos (9–10)
  r_saves_time: 'Economizo tempo na prospecção',
  r_pipeline: 'Aumentou meu pipeline',
  r_accurate: 'Listas certeiras',
  r_whatsapp: 'Contatos com WhatsApp',
  r_easy: 'Fácil de usar',
  r_value: 'Bom custo-benefício',
} as const;

export type ReviewKey = keyof typeof REVIEW_PT;

/** Texto em português do motivo (o que vai para o backend). */
export function reasonPt(id: string): string {
  return (REVIEW_PT as Record<string, string>)[`r_${id}`] ?? id;
}

export function useReviewText() {
  const { t } = useTranslation();
  const tx = useCallback(
    (key: ReviewKey, vars?: Record<string, string | number>): string =>
      t(`rv_${key}`, {
        defaultValue: REVIEW_PT[key],
        ...vars,
        interpolation: { escapeValue: false },
      }) as string,
    [t],
  );
  const reason = useCallback((id: string): string => tx(`r_${id}` as ReviewKey), [tx]);
  return { tx, reason };
}
